/* ==========================================================================
   TRE EMME — CX core: overlays, header, mega menu, ticker, hero, rails
   Vanilla, no dependencies. Works alongside the legacy bundle.
   ========================================================================== */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Same output as Liquid's `money`: the shop's own format (window.CX_MONEY_FORMAT, set in cx-head). Shopify.formatMoney
  // from apps is not used: without a format it falls back to "$".
  const money = (cents) => {
    const fmt = window.CX_MONEY_FORMAT;
    const n = Number(cents) || 0;
    const sep = (dec, th, places = 2) => {
      const [i, d] = (n / 100).toFixed(places).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? dec + d : '');
    };
    if (fmt && /\{\{\s*\w+\s*\}\}/.test(fmt)) {
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => ({
        amount: sep('.', ','),
        amount_no_decimals: sep('.', ',', 0),
        amount_with_comma_separator: sep(',', '.'),
        amount_no_decimals_with_comma_separator: sep(',', '.', 0),
        amount_with_apostrophe_separator: sep('.', "'"),
        amount_with_space_separator: sep(',', ' ')
      }[key] || sep(',', '.'))).replace(/&euro;/g, '€').replace(/&nbsp;/g, ' ');
    }
    return (n / 100).toLocaleString('it-IT', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' });
  };

  /* Toast */
  let toastTimer;
  // toast(message, { type: 'info' | 'success' | 'error' })
  const toast = (message, opts = {}) => {
    let el = $('.cx-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cx-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.innerHTML = '<span class="cx-toast__dot" aria-hidden="true"></span><span class="cx-toast__text"></span>';
      document.body.appendChild(el);
    }
    const type = opts.type || 'info';
    el.dataset.type = type;
    $('.cx-toast__text', el).textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), type === 'error' ? 5000 : 3200);
  };

  /* Overlay manager — one open layer at a time, focus trap, Esc, scroll lock */
  // let: the theme editor re-renders the header section, see shopify:section:load below
  let scrim = $('[data-cx-scrim]');
  let header = $('[data-cx-header]');
  let openLayer = null;
  let lastFocus = null;

  const focusables = (el) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
    .filter((n) => n.getClientRects().length > 0 || n === document.activeElement);

  const trap = (e) => {
    if (!openLayer || e.key !== 'Tab') return;
    const items = focusables(openLayer);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const setTriggers = (id, expanded) => $$(`[data-cx-open="${id}"]`).forEach((t) => t.setAttribute('aria-expanded', String(expanded)));

  const openOverlay = (layer, opener) => {
    if (!layer) return;
    if (openLayer && openLayer !== layer) closeOverlay(false);
    closeMega();
    // Safari and touch taps do not focus buttons: fall back to the trigger itself
    const active = document.activeElement;
    lastFocus = opener || (active && active !== document.body ? active : null);
    openLayer = layer;
    layer.classList.add('is-open');
    root.classList.add('cx-lock');
    setTriggers(layer.id, true);
    if (scrim) scrim.classList.add('is-visible');
    const target = $('input[type="search"]', layer) || focusables(layer)[0];
    setTimeout(() => target && target.focus({ preventScroll: true }), 60);
    document.addEventListener('keydown', trap);
  };

  const closeOverlay = (restore = true) => {
    if (!openLayer) return;
    const layer = openLayer;
    layer.classList.remove('is-open');
    setTriggers(layer.id, false);
    openLayer = null;
    root.classList.remove('cx-lock');
    scrim && scrim.classList.remove('is-visible');
    document.removeEventListener('keydown', trap);
    if (restore && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cx-open]');
    if (opener) {
      e.preventDefault();
      const layer = document.getElementById(opener.getAttribute('data-cx-open'));
      if (layer === openLayer) closeOverlay(); else openOverlay(layer, opener);
      return;
    }
    if (e.target.closest('[data-cx-close]')) { e.preventDefault(); closeOverlay(); return; }
    if (openLayer && e.target === scrim) { closeOverlay(); return; }
    if (openLayer && openLayer.id === 'cx-search' && !e.target.closest('.cx-search__panel') && !e.target.closest('[class*="boost-pfs"]')) closeOverlay();
    if (openLayer && openLayer.classList.contains('cx-drawer') && !e.target.closest('.cx-drawer__panel')) closeOverlay();
  });

  /* Header — height var, scrolled state, hide on scroll down */
  if (header) {
    // Header and bar heights are design tokens in CSS; only flag a missing bar
    if (!$('.cx-bar')) root.style.setProperty('--cx-bar-h', '0px');

    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const goingDown = y > lastY + 4;
      const goingUp = y < lastY - 4;
      if (goingDown && y > 240 && !openLayer) header.classList.add('is-hidden');
      else if (goingUp || y < 120) header.classList.remove('is-hidden');
      if (goingDown || goingUp) lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    onScroll();
  }

  /* PayPal instalments line (legacy markup, also re-rendered in the cart drawer): the &nbsp; around the amount make
     double gaps, so text nodes are normalised to single spaces */
  const tidyInstalments = (rootEl) => {
    $$('.scalapay p', rootEl).forEach((p) => {
      p.childNodes.forEach((n) => { if (n.nodeType === 3) n.nodeValue = n.nodeValue.replace(/[\s\u00a0]+/g, ' '); });
    });
  };
  tidyInstalments(document);
  const drawerEl = document.getElementById('drawer');
  if (drawerEl) new MutationObserver(() => tidyInstalments(drawerEl)).observe(drawerEl, { childList: true, subtree: true });

  /* Wishlist count — mirrors the hidden Swish <wishlist-link> (it re-renders whenever the wishlist changes) */
  const wishSource = $('[data-cx-wish-source]');
  if (wishSource) {
    const wishOut = $$('[data-cx-wish-count]');
    const wishParts = $$('[data-cx-wish-part]');
    const syncWish = () => {
      const n = (wishSource.wishlist && wishSource.wishlist.numItems) || 0;
      // same "( n )" as the cart count; hidden while the list is empty
      wishOut.forEach((el) => { el.textContent = n || ''; el.hidden = !n; });
      wishParts.forEach((el) => { el.hidden = !n; });
    };
    new MutationObserver(syncWish).observe(wishSource, { subtree: true, childList: true, characterData: true, attributes: true });
    customElements.whenDefined('wishlist-link').then(syncWish);
  }

  /* Mega menu — hover intent on desktop, click + keyboard everywhere */
  const finePointerNav = window.matchMedia('(hover: hover) and (pointer: fine)');
  let openItem = null;
  let openedAt = 0;
  let openTimer;
  let closeTimer;

  function openMega(item) {
    if (openItem === item) return;
    if (openItem) {
      openItem.classList.remove('is-open');
      $('.cx-nav__trigger', openItem).setAttribute('aria-expanded', 'false');
    }
    openItem = item;
    openedAt = Date.now();
    item.classList.add('is-open');
    $('.cx-nav__trigger', item).setAttribute('aria-expanded', 'true');
    header && header.classList.add('is-open');
    if (!openLayer && scrim) scrim.classList.add('is-visible');
  }

  function closeMega() {
    clearTimeout(openTimer);
    if (!openItem) return;
    openItem.classList.remove('is-open');
    $('.cx-nav__trigger', openItem).setAttribute('aria-expanded', 'false');
    openItem = null;
    header && header.classList.remove('is-open');
    if (!openLayer && scrim) scrim.classList.remove('is-visible');
  }

  const bindMega = (item) => {
    const trigger = $('.cx-nav__trigger', item);
    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(closeTimer);
      openTimer = setTimeout(() => openMega(item), openItem ? 0 : 90);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(closeMega, 180);
    });
    trigger.addEventListener('click', (e) => {
      // with a mouse, gender triggers (Donna, Uomo) go to their new arrivals; hover already shows the panel
      if (trigger.dataset.cxHref && e.detail > 0 && finePointerNav.matches) { window.location.href = trigger.dataset.cxHref; return; }
      // a click right after hover-intent opened the panel should not close it
      if (openItem === item) { if (Date.now() - openedAt > 600) closeMega(); }
      else openMega(item);
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget) && openItem === item) closeMega();
    });
  };
  const bindScrim = () => scrim && scrim.addEventListener('pointerenter', () => { if (openItem) closeMega(); });
  $$('[data-cx-mega-item]').forEach(bindMega);
  bindScrim();

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (openItem) {
      const trigger = $('.cx-nav__trigger', openItem);
      closeMega();
      trigger && trigger.focus();
    } else if (openLayer) {
      closeOverlay();
    }
  });

  const markLoaded = (img) => img.classList.add('is-loaded');

  /* Product videos (video as first media). Liquid only exposes the 1080p mp4, so the HLS stream is played instead:
     the player picks the rendition for the box it fills (480p in a card). Sources attach near the viewport,
     playback pauses off-screen. hls.js is preferred even where HLS plays natively (Safari, Chrome): native players pick
     the rendition by bandwidth, so a card would stream 1080p. Native HLS, then the mp4, are the fallbacks. */
  let hlsLib;
  const loadHls = () => hlsLib || (hlsLib = new Promise((ok, ko) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.light.min.js';
    s.onload = () => (window.Hls && window.Hls.isSupported() ? ok(window.Hls) : ko());
    s.onerror = ko;
    document.head.append(s);
  }));
  const playVideo = (v) => v.play().catch(() => {});
  const attachVideo = (v) => {
    v.dataset.cxAttached = '1';
    const { hls, mp4 } = v.dataset;
    const fallback = () => { v.src = hls && v.canPlayType('application/vnd.apple.mpegurl') ? hls : mp4; playVideo(v); };
    if (!hls) return fallback();
    loadHls().then((Hls) => {
      const player = new Hls({ capLevelToPlayerSize: true });
      player.loadSource(hls);
      player.attachMedia(v);
      playVideo(v);
    }).catch(fallback);
  };
  const videoIO = 'IntersectionObserver' in window && new IntersectionObserver((entries) => entries.forEach(({ target: v, isIntersecting }) => {
    if (!isIntersecting) v.pause();
    else if (v.dataset.cxAttached) playVideo(v);
    else attachVideo(v);
  }), { rootMargin: '200px 0px' });
  // Reduced motion: no autoplay, the still underneath (card) or the poster (PDP) stays
  const watchVideos = (scope) => reduceMotion.matches || $$('video[data-cx-video]:not([data-cx-watched])', scope).forEach((v) => {
    v.dataset.cxWatched = '1';
    if (videoIO) videoIO.observe(v); else attachVideo(v);
  });

  const watchImages = (scope = document) => {
    watchVideos(scope);
    $$('img.cx-img:not(.is-loaded)', scope).forEach((img) => {
      if (img.complete && img.naturalWidth) markLoaded(img);
      else {
        img.addEventListener('load', () => markLoaded(img), { once: true });
        img.addEventListener('error', () => markLoaded(img), { once: true });
      }
    });
  };

  /* Ticker — duplicates content so the loop is seamless, pauses offscreen.
     [data-cx-ticker-defer] waits for its content (e.g. the Instagram wall fed by Instafeed) and is started by cx-home.js. */
  const initTicker = (ticker) => {
    const track = $('.cx-ticker__track', ticker);
    if (!track || reduceMotion.matches || ticker.classList.contains('is-running')) return;
    const base = Array.from(track.children);
    // measure the items themselves: with the static min-width: 100% the track looked full while its items were not,
    // and the leftover space showed up as a bigger gap once per loop
    track.style.minWidth = '0';
    let guard = 0;
    while (track.scrollWidth < ticker.offsetWidth && guard < 8) {
      base.forEach((n) => track.appendChild(n.cloneNode(true)));
      guard += 1;
    }
    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    $$('a', clone).forEach((a) => a.setAttribute('tabindex', '-1'));
    ticker.appendChild(clone);
    // cloneNode drops the load listeners: copies of images still loading would stay at opacity 0 (empty boxes in the loop)
    watchImages(ticker);
    ticker.classList.add('is-running');
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => ticker.classList.toggle('is-paused', !entry.isIntersecting)).observe(ticker);
    }
    ticker.addEventListener('focusin', () => ticker.classList.add('is-paused'));
    ticker.addEventListener('focusout', () => ticker.classList.remove('is-paused'));
  };
  $$('[data-cx-ticker]:not([data-cx-ticker-defer])').forEach(initTicker);

  /* Theme editor: every setting or menu change re-renders the header section with new nodes, so rebind them
     (otherwise the mega menu stops opening on hover until the page is reloaded) */
  document.addEventListener('shopify:section:load', (e) => {
    if (!$('[data-cx-header]', e.target)) return;
    header = $('[data-cx-header]');
    scrim = $('[data-cx-scrim]');
    openItem = null;
    $$('[data-cx-mega-item]', e.target).forEach(bindMega);
    bindScrim();
    $$('[data-cx-ticker]:not([data-cx-ticker-defer])', e.target).forEach(initTicker);
  });

  /* Reveal on scroll & image fade-in */
  const revealEls = $$('[data-cx-reveal]');
  if ('IntersectionObserver' in window && !reduceMotion.matches) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-in'));
  }

  watchImages();

  /* Rails — tabs, arrows, progress */
  const initRail = (rail) => {
    // Lazy rails (recommendations) are empty at load: only mark ready once there is a track to wire, or the later call is a no-op
    if (rail.dataset.cxRailReady || !$('[data-cx-track]', rail)) return;
    rail.dataset.cxRailReady = '1';
    const tabs = $$('[data-cx-tab]', rail);
    const panels = $$('[data-cx-panel]', rail);
    const prev = $('[data-cx-prev]', rail);
    const next = $('[data-cx-next]', rail);
    let active = panels.find((p) => !p.hidden) || panels[0];

    const track = () => active && $('[data-cx-track]', active);

    const update = () => {
      const t = track();
      if (!t) return;
      const max = t.scrollWidth - t.clientWidth;
      const ratio = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
      const progress = max > 0 ? t.scrollLeft / max : 0;
      const bar = $('[data-cx-progress]', active);
      if (bar) {
        bar.style.setProperty('--cx-progress-w', `${Math.max(ratio * 100, 8)}%`);
        bar.style.setProperty('--cx-progress-x', `${progress * (1 / Math.max(ratio, .08) - 1) * 100}%`);
      }
      if (prev) prev.disabled = t.scrollLeft <= 2;
      if (next) next.disabled = t.scrollLeft >= max - 2;
    };

    const step = (dir) => {
      const t = track();
      if (!t) return;
      const item = $('.cx-rail__item', t);
      const gap = parseFloat(getComputedStyle(t).columnGap) || 0;
      const per = item ? item.getBoundingClientRect().width + gap : t.clientWidth;
      const visible = Math.max(1, Math.floor((t.clientWidth + gap) / per));
      t.scrollBy({ left: dir * per * visible, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    prev && prev.addEventListener('click', () => step(-1));
    next && next.addEventListener('click', () => step(1));

    panels.forEach((panel) => {
      const t = $('[data-cx-track]', panel);
      if (!t) return;
      t.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      $$('.cx-rail__item', t).forEach((item, i) => item.style.setProperty('--i', Math.min(i, 6)));
    });

    // Tabs after the first arrive empty (data-cx-lazy): fetch their cards once, on open or when the visitor hovers the tab
    const loadPanel = (p) => {
      const url = p && p.dataset.cxLazy;
      if (!url) return;
      delete p.dataset.cxLazy;
      const t = $('[data-cx-track]', p);
      fetch(url)
        .then((r) => r.json())
        .then((data) => {
          const doc = new DOMParser().parseFromString(data['cx-rail-items'] || '', 'text/html');
          const items = doc.querySelector('[data-cx-rail-items]');
          if (!items || !t) return;
          t.replaceChildren(...Array.from(items.children));
          $$('.cx-rail__item', t).forEach((item, i) => item.style.setProperty('--i', Math.min(i, 6)));
          watchImages(t);
          update();
        })
        .catch(() => { p.dataset.cxLazy = url; });
    };
    tabs.forEach((tab) => {
      const warm = () => loadPanel(panels.find((p) => p.id === tab.getAttribute('aria-controls')));
      tab.addEventListener('pointerenter', warm, { once: true });
      tab.addEventListener('focus', warm, { once: true });
    });

    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p) => {
        const on = p.id === tab.getAttribute('aria-controls');
        if (on) loadPanel(p);
        p.hidden = !on;
        if (on) {
          active = p;
          p.classList.remove('is-entering');
          void p.offsetWidth;
          p.classList.add('is-entering');
          const t = $('[data-cx-track]', p);
          if (t) t.scrollLeft = 0;
        }
      });
      if (focus) tab.focus();
      update();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        select(tabs[n], true);
      });
    });

    window.addEventListener('resize', () => requestAnimationFrame(update), { passive: true });
    // First read in the next frame: right after the --i writes above (and the page's own DOM work) a synchronous
    // read of scrollWidth forced an extra layout per rail
    requestAnimationFrame(update);
  };
  $$('[data-cx-rail]').forEach(initRail);

  /* Scalapay widget: logo in line with the text (its styles live in a shadow root) */
  const tuneScalapay = (tries = 0) => {
    const widgets = $$('scalapay-widget');
    let pending = false;
    widgets.forEach((w) => {
      const root = w.shadowRoot;
      if (!root || !root.querySelector('.sp-logo')) { pending = true; return; }
      if (root.querySelector('style[data-cx]')) return;
      const st = document.createElement('style');
      st.setAttribute('data-cx', '');
      // Flex wrappers ignore vertical-align, so make the logo inline. The wordmark baseline sits at 74% of the SVG height:
      // dropping the SVG by the remaining 26% (x 1.3em) puts it on the text baseline. Gaps become one normal word space.
      st.textContent = '.sp--nowrap{margin-right:0!important}.sp-link,.sp-logo{display:inline!important}'
        + '.sp-logo svg{display:inline-block!important;width:auto!important;height:1.3em!important;vertical-align:-.337em!important}'
        + '.sp-icon{display:inline-block!important;margin-left:.15em!important;vertical-align:middle}.sp-icon svg{display:block;width:.7em;height:.7em}'
        + '.sp-price{font-size:inherit!important;font-weight:500!important}';
      root.appendChild(st);
    });
    if (pending && tries < 20) setTimeout(() => tuneScalapay(tries + 1), 300);
  };
  if (document.querySelector('scalapay-widget')) window.addEventListener('load', () => tuneScalapay());

  /* Newsletter popup — non-modal card (Figma): after N seconds or half a page of scrolling, once.
     Closed = quiet for N days, subscribed = never again. No scrim, no focus trap: the page stays usable. */
  const pop = document.querySelector('[data-cx-newspop]');
  if (pop) {
    const KEY = 'cx-newspop';
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
    const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode: show again next visit */ } };
    const state = read();
    const days = Number(pop.dataset.days) || 14;
    const quiet = state && (state.done || Date.now() - state.t < days * 864e5);
    // Right after the header in the DOM, so keyboard and screen-reader users reach it first
    const headerEl = document.querySelector('.cx-header');
    if (headerEl && headerEl.parentElement) headerEl.parentElement.insertBefore(pop, headerEl.nextSibling);
    let shown = false;
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    const hide = () => {
      if (!pop.classList.contains('is-open')) return;
      pop.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      setTimeout(() => { pop.hidden = true; }, 340);
      if (!(read() || {}).done) write({ t: Date.now() });
    };
    const show = () => {
      if (shown) return;
      if (openLayer) { setTimeout(show, 4000); return; } // never over the menu, cart or filters: try again shortly
      shown = true;
      // Seen counts as "not now": a non-modal card that is simply ignored must not come back on every page
      if (!(read() || {}).done) write({ t: Date.now() });
      window.removeEventListener('scroll', onScrollPop);
      pop.hidden = false;
      requestAnimationFrame(() => pop.classList.add('is-open'));
      document.addEventListener('keydown', onKey);
    };
    const onScrollPop = () => {
      const h = document.documentElement.scrollHeight - innerHeight;
      if (h > 0 && scrollY / h > .5) show();
    };
    if (!quiet && !/^\/(checkout|cart|account)/.test(location.pathname)) {
      setTimeout(show, (Number(pop.dataset.delay) || 12) * 1000);
      window.addEventListener('scroll', onScrollPop, { passive: true });
    }
    pop.querySelector('[data-cx-newspop-close]').addEventListener('click', hide);
    // Footer "Iscriviti" opens the card on demand, whatever the timer or memory say
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cx-newspop-open]');
      if (!t) return;
      e.preventDefault();
      shown = false;
      show();
      const field = pop.querySelector('input[type="email"]');
      if (field) setTimeout(() => field.focus({ preventScroll: true }), 360);
    });

    pop.addEventListener('cx:subscribed', () => {
      write({ t: Date.now(), done: true });
    });
    // Klaviyo embedded form (Theme settings > Newsletter): a sign-up anywhere stops the popup too
    window.addEventListener('klaviyoForms', (e) => {
      if (e.detail && e.detail.type === 'submit') write({ t: Date.now(), done: true });
    });
  }

  /* Newsletter forms (popup + band): subscribe straight to the Klaviyo list through Klaviyo's public client API.
     Every [data-cx-klaviyo] form carries data-company (public key) and data-list; the genere choice goes on the profile. */
  const subscribe = async (form) => {
    const email = (form.elements.email && form.elements.email.value || '').trim();
    const gender = form.elements.genere ? form.elements.genere.value : '';
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: form.dataset.source || 'Sito Tre Emme',
          profile: { data: { type: 'profile', attributes: { email, properties: gender ? { genere: gender } : {} } } },
        },
        relationships: { list: { data: { type: 'list', id: form.dataset.list } } },
      },
    };
    const r = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(form.dataset.company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2024-10-15' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(String(r.status));
  };
  $$('[data-cx-klaviyo]').forEach((form) => {
    const msg = form.querySelector('[data-cx-news-msg]');
    const btn = form.querySelector('[type="submit"]');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (btn.dataset.done) { const p = form.closest('[data-cx-newspop]'); p && p.querySelector('[data-cx-newspop-close]').click(); return; }
      const email = form.elements.email;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.value.trim())) { msg.textContent = 'Inserisci un indirizzo email valido.'; email.focus(); return; }
      btn.disabled = true;
      btn.classList.add('is-loading');
      msg.textContent = '';
      try {
        await subscribe(form);
        form.classList.add('is-done');
        const wrap = form.querySelector('[data-cx-news-fields]');
        const done = form.querySelector('[data-cx-news-done]');
        if (wrap) wrap.hidden = true;
        if (done) done.hidden = false;
        const label = btn.querySelector('[data-cx-news-label]');
        if (label && form.dataset.doneLabel) { label.textContent = form.dataset.doneLabel; btn.dataset.done = '1'; }
        else btn.hidden = true;
        form.dispatchEvent(new CustomEvent('cx:subscribed', { bubbles: true }));
        try { localStorage.setItem('cx-newspop', JSON.stringify({ t: Date.now(), done: true })); } catch (err) { /* private mode */ }
      } catch (err) {
        msg.textContent = 'Non siamo riusciti a completare l\'iscrizione. Riprova tra poco.';
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    });
  });

  /* Google reviews (cx-reviews section): read the Reputon widget rendered off-screen and draw CX cards.
     After 10 s without reviews the app widget itself is shown instead (.is-fallback). */
  $$('[data-cx-revs]').forEach((section) => {
    const source = $('[data-cx-revs-source]', section);
    const track = $('[data-cx-track]', section);
    if (!source || !track) return;
    const text = (el, sel) => { const e = el && el.querySelector(sel); return e ? e.textContent.replace(/\s+/g, ' ').trim() : ''; };
    const read = () => {
      const w = $('.reputon-google-reviews-widget', source);
      if (!w) return null;
      const items = $$('.reputon-single-review', w).map((r) => {
        const starsEl = $('.reputon-rating-stars', r);
        const stars = starsEl ? Math.min($$('svg', starsEl).length, 5) : 5;
        const name = text(r, '.reputon-name');
        const date = text(r, '.reputon-date');
        const body = text(r, '.reputon-text');
        return { stars, name, date, body };
      }).filter((x) => x.body && x.name);
      return {
        items,
        rating: text(w, '.reputon-count-number'),
        count: text(w, '.reputon-reviews-count').replace(/\s*su\s*$/i, ''),
        link: ($('.reputon-link', w) || {}).href || '',
        write: ($('.reputon-leave-review', w) && $('.reputon-leave-review', w).closest('a') || {}).href || '',
      };
    };
    let done = false;
    const build = (d) => {
      done = true;
      obs.disconnect();
      if (!d || d.items.length < 2) { section.classList.add('is-fallback'); return; }
      const seen = new Set();
      const cards = d.items.filter((x) => { const k = x.name + x.body.slice(0, 30); if (seen.has(k)) return false; seen.add(k); return true; });
      track.replaceChildren(...cards.map((x) => {
        const li = document.createElement('li');
        li.className = 'cx-rail__item';
        const card = document.createElement('article');
        card.className = 'cx-review';
        const st = document.createElement('p');
        st.className = 'cx-review__stars';
        st.textContent = '★'.repeat(x.stars) + '☆'.repeat(5 - x.stars);
        st.setAttribute('aria-label', `${x.stars} stelle su 5`);
        const q = document.createElement('blockquote');
        q.className = 'cx-review__text';
        q.textContent = x.body; // review text comes from Google: never as HTML
        const foot = document.createElement('footer');
        foot.className = 'cx-review__foot';
        const n = document.createElement('span'); n.className = 'cx-review__name'; n.textContent = x.name;
        const dt = document.createElement('span'); dt.className = 'cx-review__date'; dt.textContent = x.date;
        foot.append(n, dt);
        card.append(st, q, foot);
        li.appendChild(card);
        return li;
      }));
      const head = $('[data-cx-revs-head]', section);
      if (head) {
        $('[data-cx-revs-rating]', head).textContent = d.rating || '';
        $('[data-cx-revs-count]', head).textContent = d.count || '';
        const write = $('[data-cx-revs-write]', head);
        if (d.write) write.href = d.write; else if (d.link) write.href = d.link; else write.hidden = true;
        head.hidden = false;
      }
      section.classList.add('is-live');
      initRail(section);
      // The app's carousel keeps animating off-screen (it was most of the page's main-thread time): drop it once read.
      // In the theme editor it stays, so the app block can still be configured.
      if (!source.classList.contains('is-editing')) source.replaceChildren();
    };
    const obs = new MutationObserver(() => { const d = read(); if (d && d.items.length >= 3 && !done) build(d); });
    obs.observe(source, { childList: true, subtree: true });
    const first = read();
    if (first && first.items.length >= 3) build(first);
    setTimeout(() => { if (!done) build(read()); }, 10000);
  });

  /* Shared helpers for cx-commerce.js */
  window.CX = { $, $$, toast, focusables, money, reduceMotion, watchImages, initRail, openOverlay, closeOverlay, initTicker };

  document.addEventListener('shopify:section:load', (e) => watchImages(e.target));
})();